import React from 'react';
import { Link } from 'react-router-dom';
import { PlatformSubPageHeroImage } from '../platform/subpages';
import { solutionHeroWidgets } from './solutionHeroWidgets';
import { SolutionHeroWidgetCard } from './SolutionHeroWidgetCard';
import type { SolutionSubpageConfig } from './types';

const SPLIT_BG_SLUGS = new Set([
  'smart-house',
  'smart-manufacturing',
  'smart-hospitality',
  'commercial-lighting',
]);

export const SolutionHero: React.FC<{ config: SolutionSubpageConfig }> = ({ config }) => {
  const widgets = solutionHeroWidgets[config.slug];
  const isSplitBg = SPLIT_BG_SLUGS.has(config.slug);

  if (isSplitBg) {
    const backdrop = config.heroBackdrop;
    const hasBackdrop = Boolean(backdrop);

    return (
      <section
        className={`platform-sub-hero solution-hero solution-hero--premium solution-hero--split-bg solution-hero--split-columns${hasBackdrop ? ' solution-hero--media-bg' : ''} solution-hero--${config.slug}`}
        aria-labelledby={`platform-sub-${config.slug}-title`}
      >
        <div className="solution-hero__card">
          {backdrop ? (
            <div className="solution-hero__media-bg" aria-hidden>
              {backdrop.kind === 'video' ? (
                <video
                  className="solution-hero__media-bg-el"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster={config.heroImage.src}
                >
                  <source src={backdrop.src} type={backdrop.type ?? 'video/mp4'} />
                </video>
              ) : (
                <img
                  className="solution-hero__media-bg-el"
                  src={backdrop.src}
                  alt=""
                  loading="eager"
                  decoding="async"
                />
              )}
            </div>
          ) : null}
          <div className="solution-hero__card-overlay" aria-hidden />
          {hasBackdrop && widgets?.length ? (
            <div className="solution-hero__widgets-overlay" aria-hidden>
              {widgets.map((widget) => (
                <SolutionHeroWidgetCard key={`${widget.position}-${widget.label}`} widget={widget} />
              ))}
            </div>
          ) : null}
          <div className="solution-hero__card-inner">
            <div className="solution-hero__card-content">
              <p className="solution-hero__eyebrow solution-hero__eyebrow--premium">
                {config.premium.heroEyebrow}
              </p>
              <h1
                id={`platform-sub-${config.slug}-title`}
                className="platform-sub-hero__title solution-hero__title--premium"
              >
                {config.hero.title}
              </h1>
              <p className="platform-sub-hero__subtitle solution-hero__subtitle--premium">
                {config.hero.subtitle}
              </p>
              <div className="platform-sub-hero__actions solution-hero__actions--premium">
                <Link to={config.hero.primaryCta.to} className="solution-hero__cta-premium">
                  {config.hero.primaryCta.label}
                </Link>
              </div>
            </div>
            {!hasBackdrop ? (
              <div className="solution-hero__visual-col">
                <div className="solution-hero__visual-frame">
                  <img
                    className="solution-hero__visual-img"
                    src={config.heroImage.src}
                    alt={config.heroImage.alt}
                    width={config.heroImage.width}
                    height={config.heroImage.height}
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`platform-sub-hero solution-hero solution-hero--premium solution-hero--${config.slug}`}
      aria-labelledby={`platform-sub-${config.slug}-title`}
    >
      <div className="solution-hero__mesh" aria-hidden />
      <div className="platform-sub-hero__inner solution-hero__inner">
        <div className="platform-sub-hero__content solution-hero__content">
          <p className="solution-hero__eyebrow solution-hero__eyebrow--premium">{config.premium.heroEyebrow}</p>
          <h1 id={`platform-sub-${config.slug}-title`} className="platform-sub-hero__title solution-hero__title--premium">
            {config.hero.title}
          </h1>
          <p className="platform-sub-hero__subtitle solution-hero__subtitle--premium">{config.hero.subtitle}</p>
          <div className="platform-sub-hero__actions solution-hero__actions--premium">
            <Link to={config.hero.primaryCta.to} className="solution-hero__cta-premium">
              {config.hero.primaryCta.label}
            </Link>
          </div>
        </div>
        <div className="platform-sub-hero__visual solution-hero__visual-col">
          <div className="platform-sub-hero-visual solution-hero__stage">
            <div className="solution-hero__stage-grid" aria-hidden />
            <div className="solution-hero__stage-blob" aria-hidden />
            <PlatformSubPageHeroImage
              src={config.heroImage.src}
              alt={config.heroImage.alt}
              width={config.heroImage.width}
              height={config.heroImage.height}
            />
            {widgets?.map((widget) => (
              <SolutionHeroWidgetCard key={`${widget.position}-${widget.label}`} widget={widget} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
